// Globals
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IFaq } from '@/models/faq.model';

// Modules
import { selectChannel } from '@/modules/Channels';
import { getFaqChannelBySlug, useGetFaqChannels } from '@/modules/FaqChannels';
import { Header } from '@/modules/Layout';
import {
  ExportFaqsDTO,
  FaqBulkActionsBar,
  FaqTable,
  TFaqFormValues,
  useChosenFaqs,
  useExportFaqs,
  useFaqSearchFilters,
} from '@/modules/FaqCommon';
import { BulkDeleteChannelFaqModal, CreateChannelFaqModal, ImportChannelFaqModal } from '../../components';

// Components
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { PlusIcon } from '@/icons';
import { Button } from '@/ui';
import { StyledButtons, StyledContainer } from './FaqsContainerStyles';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { deleteFaqs, getFaqs, updateFaq } from '../../feature';

// Helpers
import { useBoolean } from '@/hooks';
import { FaqExportScope } from '@/models/faqImportExport.model';
import { GetFaqsDTO } from '../../dtos';
import { useGetFaqs } from '../../hooks';

const FaqsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeChannel = useAppState(selectChannel);
  const { channels: faqChannels } = useGetFaqChannels();
  const selectedChannelId = useMemo(
    () => getFaqChannelBySlug(faqChannels, activeChannel?.slug)?.channelId,
    [faqChannels, activeChannel?.slug],
  );
  const { faqs, isLoading, pagination } = useGetFaqs(selectedChannelId);
  const { handleExportFaqs, isExporting } = useExportFaqs();
  const { chosenFaqs, rowSelection, handleResetChosenFaqs } = useChosenFaqs<IFaq>();

  const { value: isCreateOpen, setTrue: openCreate, setFalse: closeCreate } = useBoolean();
  const { value: isImportOpen, setTrue: openImport, setFalse: closeImport } = useBoolean();
  const { value: isBulkDeleteOpen, setTrue: openBulkDelete, setFalse: closeBulkDelete } = useBoolean();

  const handleSearch = useCallback(
    (filters: { question?: string; answer?: string }) => {
      if (!selectedChannelId) return;
      dispatch(getFaqs(new GetFaqsDTO({ channelId: selectedChannelId, page: 1, ...filters })));
    },
    [dispatch, selectedChannelId],
  );

  const { filters, handleFilterChange } = useFaqSearchFilters(handleSearch);

  const handlePageChange = (page: number) => {
    if (!selectedChannelId) return;
    dispatch(
      getFaqs(
        new GetFaqsDTO({
          channelId: selectedChannelId,
          page,
          question: filters.question,
          answer: filters.answer,
        }),
      ),
    );
  };

  const handleFaqsReload = useCallback(() => {
    if (!selectedChannelId) return;
    dispatch(
      getFaqs(
        new GetFaqsDTO({
          channelId: selectedChannelId,
          page: pagination?.page ?? 1,
          question: filters.question,
          answer: filters.answer,
        }),
      ),
    );
  }, [dispatch, selectedChannelId, pagination?.page, filters]);

  const handleEditFaq = async (faqId: string, values: TFaqFormValues) => {
    if (!selectedChannelId) return;
    try {
      await dispatch(updateFaq({ ...values, faqId, channelId: selectedChannelId })).unwrap();
    } catch {}
  };

  const handleDeleteFaq = async (faqId: string) => {
    if (!selectedChannelId) return;
    try {
      await dispatch(deleteFaqs({ channelId: selectedChannelId, faqIds: [faqId] })).unwrap();
      handleFaqsReload();
    } catch {}
  };

  const handleExport = () => {
    if (!selectedChannelId) return;
    handleExportFaqs(
      new ExportFaqsDTO({
        scope: FaqExportScope.CHANNEL,
        channelId: selectedChannelId,
        question: filters.question,
        answer: filters.answer,
      }),
    );
  };

  return (
    <>
      <Header title={t('menu_channel_faq')} suptitle={t('menu_faqs')}>
        <StyledButtons>
          <Button icon={<UploadOutlined />} type="default" loading={isExporting} onClick={handleExport}>
            {t('btn_faqs_export')}
          </Button>
          <Button icon={<DownloadOutlined />} type="default" onClick={openImport}>
            {t('btn_faqs_import')}
          </Button>
          <Button icon={<PlusIcon />} type="primary" onClick={openCreate}>
            {t('btn_faqs_create')}
          </Button>
        </StyledButtons>
      </Header>
      <StyledContainer>
        <FaqBulkActionsBar selectedCount={chosenFaqs.length} onDeleteSelected={openBulkDelete} />
        <FaqTable
          data={faqs}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={handlePageChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          onEditFaq={handleEditFaq}
          onDeleteFaq={handleDeleteFaq}
          rowSelection={rowSelection}
        />
      </StyledContainer>
      {selectedChannelId && (
        <>
          <CreateChannelFaqModal isOpen={isCreateOpen} onClose={closeCreate} channelId={selectedChannelId} />
          <ImportChannelFaqModal isOpen={isImportOpen} onClose={closeImport} channelId={selectedChannelId} />
          <BulkDeleteChannelFaqModal
            isOpen={isBulkDeleteOpen}
            onClose={closeBulkDelete}
            channelId={selectedChannelId}
            chosenFaqIds={chosenFaqs}
            resetSelection={handleResetChosenFaqs}
            onDeleted={handleFaqsReload}
          />
        </>
      )}
    </>
  );
};

export default FaqsContainer;
